<template>
  <div class="businessunitPanel">
    <b-tabs class="tabs-title">

      <!-- ── Main settings tab ── -->
      <b-tab :title="branchAlias" active>
        <div class="mt-2" v-if="loaded">
          <validation-observer ref="refFormObserver">

            <!-- ═══════════════════════════════════════════════════
                 SECTION 1 — Contact Information
            ═══════════════════════════════════════════════════ -->
            <b-card no-body class="settings-section mb-2">
              <b-card-header class="section-header">
                <feather-icon icon="UserIcon" size="16" class="mr-75 text-primary"/>
                <span class="font-weight-bold">{{ msg('Contact Information') }}</span>
              </b-card-header>
              <b-card-body class="pt-1">
                <b-row>
                  <b-col cols="12" md="6" lg="4">
                    <b-form-group :label="msg('Name')" label-for="bu-name">
                      <validation-provider #default="{ errors }" :name="msg('Name')" rules="required">
                        <b-form-input id="bu-name" v-model="businessunit.name"
                                      :state="errors.length > 0 ? false : null"/>
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" md="6" lg="4">
                    <b-form-group :label="msg('Contact fullname')" label-for="bu-contact">
                      <validation-provider #default="{ errors }" :name="msg('Contact fullname')" rules="required">
                        <b-form-input id="bu-contact" v-model="businessunit.contact"
                                      :state="errors.length > 0 ? false : null"/>
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" md="6" lg="4">
                    <b-form-group :label="msg('Contact address')" label-for="bu-address">
                      <validation-provider #default="{ errors }" :name="msg('Contact address')" rules="required">
                        <b-form-input id="bu-address" v-model="businessunit.address"
                                      :state="errors.length > 0 ? false : null"/>
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" md="6" lg="4">
                    <b-form-group :label="msg('Contact phone')" label-for="bu-phone">
                      <validation-provider #default="{ errors }" :name="msg('Contact phone')" rules="required">
                        <b-form-input id="bu-phone" v-model="businessunit.phone"
                                      :state="errors.length > 0 ? false : null"/>
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" md="6" lg="4">
                    <b-form-group :label="msg('Contact email')" label-for="bu-email">
                      <validation-provider #default="{ errors }" :name="msg('Contact email')" rules="required">
                        <b-form-input id="bu-email" v-model="businessunit.email"
                                      :state="errors.length > 0 ? false : null"/>
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" md="6" lg="4">
                    <b-form-group :label="msg('Website')" label-for="bu-website">
                      <b-form-input id="bu-website" v-model="businessunit.website"/>
                    </b-form-group>
                  </b-col>
                </b-row>
              </b-card-body>
            </b-card>

            <!-- ═══════════════════════════════════════════════════
                 SECTION 2 — Financial Settings
            ═══════════════════════════════════════════════════ -->
            <b-card no-body class="settings-section mb-2">
              <b-card-header class="section-header">
                <feather-icon icon="DollarSignIcon" size="16" class="mr-75 text-success"/>
                <span class="font-weight-bold">{{ msg('Financial Settings') }}</span>
              </b-card-header>
              <b-card-body class="pt-1">
                <b-row>
                  <b-col cols="12" md="6" lg="4">
                    <b-form-group :label="msg('Currency')" label-for="bu-currency">
                      <v-select
                          id="bu-currency"
                          :dir="isRTL ? 'rtl' : 'ltr'"
                          v-model="businessunit.cur"
                          :options="[
                            { value: 'USD', label: msg('DOLLAR') },
                            { value: 'EUR', label: msg('EURO') },
                            { value: 'ILS', label: msg('SHEKEL') },
                            { value: 'GBP', label: msg('POUND') },
                          ]"
                          class="w-100"
                          :reduce="t => t.value"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" md="6" lg="4">
                    <b-form-group :label="groupAlias" label-for="bu-group">
                      <v-select
                          id="bu-group"
                          :dir="isRTL ? 'rtl' : 'ltr'"
                          v-model="businessunit.group"
                          :options="allGroups"
                          class="w-100"
                          label="name"
                          :reduce="g => g.id"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" md="6" lg="4">
                    <b-form-group :label="msg('External Payment Link')" label-for="bu-extlink">
                      <b-form-input id="bu-extlink" v-model="businessunit.externalLink"/>
                    </b-form-group>
                  </b-col>
                </b-row>

                <!-- Logo -->
                <b-row>
                  <b-col cols="12" md="8" lg="6">
                    <b-form-group label="Hotel Logo URL" label-for="bu-logo">
                      <b-form-input id="bu-logo" v-model="businessunit.logoLink"
                                    placeholder="https://example.com/logo.png"/>
                    </b-form-group>
                    <div v-if="businessunit.logoLink" class="mt-n50 mb-2">
                      <img
                          :src="businessunit.logoLink"
                          alt="Logo preview"
                          style="max-height: 48px; max-width: 180px; object-fit: contain;
                                 border-radius: 6px; background: #f8f9fa; padding: 4px;"
                      />
                    </div>
                  </b-col>
                </b-row>

                <!-- ── Management Fee (mutual exclusive) ── -->
                <hr class="my-1"/>
                <div class="fee-section">
                  <div class="d-flex align-items-center mb-1">
                    <feather-icon icon="PercentIcon" size="15" class="mr-50 text-primary"/>
                    <span class="font-weight-bold mr-1">{{ msg('Management Fee') }}</span>
                    <b-badge variant="light-info" class="small">{{ msg('Select one method') }}</b-badge>
                  </div>

                  <b-form-group class="mb-1">
                    <b-form-radio-group
                        v-model="feeMode"
                        :options="feeModeOptions"
                        buttons
                        button-variant="outline-primary"
                        size="sm"
                        @change="onFeeModeChange"
                    />
                  </b-form-group>

                  <b-row>
                    <b-col v-if="feeMode === 'percentage'" cols="12" md="6" lg="4">
                      <b-form-group
                          :label="msg('Management fee (%)')"
                          :description="msg('Percentage of monthly net revenue')"
                      >
                        <b-input-group append="%">
                          <b-form-input
                              type="number"
                              step="0.5"
                              min="0"
                              max="100"
                              v-model="businessunit.managementFee"
                              placeholder="e.g. 15"
                          />
                        </b-input-group>
                      </b-form-group>
                    </b-col>

                    <b-col v-if="feeMode === 'perUnit'" cols="12" md="6" lg="4">
                      <b-form-group
                          :label="msg('Management fee per unit (€)')"
                          :description="msg('Fixed amount per active property per month')"
                      >
                        <b-input-group append="€">
                          <b-form-input
                              type="number"
                              step="0.5"
                              min="0"
                              v-model="businessunit.unitPrice"
                              placeholder="e.g. 50"
                          />
                        </b-input-group>
                      </b-form-group>
                    </b-col>

                    <b-col cols="12" md="6" lg="4">
                      <b-form-group
                          :label="msg('Setup fee (one-time) (€)')"
                          :description="msg('One-time onboarding fee charged to the client')"
                      >
                        <b-input-group append="€">
                          <b-form-input
                              type="number"
                              step="0.5"
                              min="0"
                              v-model="businessunit.branchExt.setupFee"
                              placeholder="e.g. 500"
                          />
                        </b-input-group>
                      </b-form-group>
                    </b-col>
                  </b-row>

                  <b-alert show variant="light" class="fee-hint p-75 mb-0">
                    <feather-icon icon="InfoIcon" size="13" class="mr-50 text-info"/>
                    <small v-if="feeMode === 'percentage'">
                      {{ msg('The percentage fee is calculated on the monthly net revenue of all contracts.') }}
                    </small>
                    <small v-else>
                      {{ msg('The per-unit fee is a fixed amount multiplied by the number of active properties in this branch.') }}
                    </small>
                  </b-alert>
                </div>
              </b-card-body>
            </b-card>

            <!-- ═══════════════════════════════════════════════════
                 SECTION 3 — API & Integrations
            ═══════════════════════════════════════════════════ -->
            <b-card no-body class="settings-section mb-2">
              <b-card-header class="section-header">
                <feather-icon icon="LinkIcon" size="16" class="mr-75 text-warning"/>
                <span class="font-weight-bold">{{ msg('API & Integrations') }}</span>
              </b-card-header>
              <b-card-body class="pt-1">
                <b-row>
                  <b-col cols="12" md="6" lg="3">
                    <b-form-group label="External API" label-for="bu-extapi">
                      <v-select
                          id="bu-extapi"
                          v-model="businessunit.externalApiType"
                          :options="['HOSTAWAY', 'RENTAL', 'APALEO', 'CLOUDBEDS', 'SITEMINDER']"
                          :clearable="false"
                          placeholder="Select an option"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" md="6" lg="3">
                    <b-form-group label="KeyBox Provider" label-for="bu-kbprovider">
                      <v-select
                          id="bu-kbprovider"
                          v-model="businessunit.branchExt.keyBoxProvider"
                          :options="['Quipeng', 'Keynest']"
                          :clearable="false"
                          placeholder="Select an option"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" md="6" lg="3">
                    <b-form-group :label="msg('Price per Keybox (€)')" label-for="bu-kbprice">
                      <b-input-group append="€">
                        <b-form-input
                            id="bu-kbprice"
                            type="number"
                            step="0.01"
                            min="0"
                            v-model="businessunit.branchExt.keyboxPrice"
                            placeholder="e.g. 5.00"
                        />
                      </b-input-group>
                      <small class="text-muted">{{ msg('Monthly keybox cost per building') }}</small>
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" md="6" lg="3"/>
                </b-row>

                <b-row>
                  <b-col cols="12" md="6" lg="6">
                    <b-form-group :label="msg('Channel ID')" label-for="bu-channelid">
                      <b-form-input id="bu-channelid" v-model="businessunit.channelId"/>
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" md="6" lg="6">
                    <b-form-group :label="msg('Channel SECRET')" label-for="bu-channelsecret">
                      <b-form-input id="bu-channelsecret" v-model="businessunit.channelSecret"/>
                    </b-form-group>
                  </b-col>
                </b-row>
              </b-card-body>
            </b-card>

            <!-- ═══════════════════════════════════════════════════
                 SECTION 4 — Messaging (Twilio)
            ═══════════════════════════════════════════════════ -->
            <b-card no-body class="settings-section mb-2">
              <b-card-header class="section-header">
                <feather-icon icon="MessageSquareIcon" size="16" class="mr-75 text-info"/>
                <span class="font-weight-bold">{{ msg('Messaging (Twilio)') }}</span>
              </b-card-header>
              <b-card-body class="pt-1">
                <b-row>
                  <b-col cols="12" md="6" lg="3">
                    <b-form-group :label="msg('Twillio ID')" label-for="bu-twillioid">
                      <b-form-input id="bu-twillioid" v-model="businessunit.twillioId"/>
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" md="6" lg="3">
                    <b-form-group :label="msg('Twillio SECRET')" label-for="bu-twilliosecret">
                      <b-form-input id="bu-twilliosecret" v-model="businessunit.twillioSecret"/>
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" md="6" lg="3">
                    <b-form-group :label="msg('Twillio Phone sender')" label-for="bu-twillioPhone">
                      <b-form-input id="bu-twillioPhone" v-model="businessunit.twillioPhoneSender"/>
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" md="6" lg="3">
                    <b-form-group :label="msg('Twillio Whatsapp sender')" label-for="bu-twillioWa">
                      <b-form-input id="bu-twillioWa" v-model="businessunit.twillioWhatsappSender"/>
                    </b-form-group>
                  </b-col>
                </b-row>
              </b-card-body>
            </b-card>

          </validation-observer>
        </div>

        <!-- Error state -->
        <div v-else-if="error != null">
          <b-alert show variant="danger">
            <feather-icon icon="AlertTriangleIcon" size="16" class="mr-75"/>
            <strong>{{ error }}</strong>
          </b-alert>
        </div>

        <!-- Loading state -->
        <div v-else>
          <div style="min-height: 300px; text-align: center">
            <div style="margin-top: 20%">
              <b-spinner label="Spinning"/>
              <p class="mt-1 text-muted">{{ msg('loading') }}</p>
            </div>
          </div>
        </div>
      </b-tab>

      <!-- ── Opening times tab ── -->
      <b-tab title="Opening times">
        <perfect-scrollbar style="max-height: 550px">
          <branch-time v-model="businessunit.openingTime"/>
        </perfect-scrollbar>
      </b-tab>

    </b-tabs>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import BranchTime from '@/components/panels/branches/BranchTime'

export default {
  name: 'branchPanel',
  components: { BranchTime },
  data() {
    return {
      openingTime: {},
      loaded: false,
      error: null,
      feeMode: 'percentage',
      businessunit: {
        cur: 'EUR',
        id: 0,
        group: null,
        kycid: 0,
        mailid: 0,
        name: '',
        orgid: 1,
        companyClients: 0,
        externalLink: '',
        website: '',
        openingTime: {},
        branchExt: { keyBoxProvider: null, keyboxPrice: null },
      },
      listeners: {
        submit: () => {},
      },
    }
  },
  props: {
    id: Number,
  },
  watch: {
    id() {
      this.onLoad()
      this.error = null
    },
  },
  computed: {
    ...mapGetters('translator', ['msg']),
    ...mapGetters('branches', ['getGroups']),
    ...mapGetters('clients', ['groupAlias', 'branchAlias']),

    feeModeOptions() {
      return [
        { text: `${this.msg('Percentage')} (%)`, value: 'percentage' },
        { text: `${this.msg('Per Unit')} (€)`, value: 'perUnit' },
      ]
    },

    allGroups() {
      return Object.keys(this.getGroups).map(id => ({
        id: parseInt(id),
        name: this.getGroups[id],
      }))
    },
  },
  created() {
    this.onLoad()
    this.$root.$on('crm::submitBusinessUnit', (this.listeners.submit = () => {
      this.submit()
    }))
  },
  beforeDestroy() {
    this.$root.$off('crm::submitBusinessUnit', this.listeners.submit)
  },
  methods: {
    ...mapActions('branches', { _getBusinessUnit: 'get', _updateBusinessUnit: 'update', _register: 'register' }),
    ...mapActions('branches', { _createRentalAccount: 'createRentalAccount' }),

    onFeeModeChange(newMode) {
      if (newMode === 'percentage') {
        this.businessunit.unitPrice = null
      } else {
        this.businessunit.managementFee = null
      }
    },

    detectFeeMode() {
      if (this.businessunit.unitPrice != null && this.businessunit.unitPrice > 0) {
        this.feeMode = 'perUnit'
      } else {
        this.feeMode = 'percentage'
      }
    },

    onLoad() {
      this.loaded = false
      if (this.id === 0 || typeof this.id !== 'number') {
        this.businessunit = {
          id: 0,
          group: null,
          cur: 'EUR',
          externalLink: '',
          website: '',
          branchExt: { keyBoxProvider: null, keyboxPrice: null },
        }
        this.feeMode = 'percentage'
        this.loaded = true
      } else {
        this.loadBusinessUnit()
      }
    },

    loadBusinessUnit() {
      this.loaded = false
      this._getBusinessUnit({ id: this.id })
          .then(businessunit => {
            if (!businessunit.branchExt) {
              businessunit.branchExt = { keyBoxProvider: null, keyboxPrice: null }
            }
            this.businessunit = businessunit
            this.detectFeeMode()
            this.loaded = true
          }, error => {
            this.error = error
          })
    },

    createBusinessUnit() {
      this._register({ branch: this.businessunit })
          .then(
              id => this.$emit('submitted', { status: true, server: id, id }),
              a => this.$emit('submitted', { status: false, server: a }),
          )
    },

    updateBusinessUnit() {
      this._updateBusinessUnit({ id: this.id, branch: this.businessunit })
          .then(
              a => this.$emit('submitted', { status: true, server: a }),
              a => this.$emit('submitted', { status: false, server: a }),
          )
    },

    submit() {
      if (this.id !== 0) return this.updateBusinessUnit()
      return this.createBusinessUnit()
    },
  },
}
</script>

<style scoped>
.settings-section {
  border: 1px solid rgba(0, 0, 0, 0.07);
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.section-header {
  background: rgba(0, 0, 0, 0.02);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  padding: 0.75rem 1.25rem;
  display: flex;
  align-items: center;
}

.fee-section {
  background: rgba(115, 103, 240, 0.03);
  border-radius: 6px;
  padding: 1rem;
}

.fee-hint {
  background: rgba(0, 149, 255, 0.05) !important;
  border: 1px solid rgba(0, 149, 255, 0.15) !important;
  border-radius: 4px;
}

.customColorLabelWhatsapp {
  color: #25D366 !important;
  font-weight: bold;
}
</style>
